<template>
  <div class="container mortgage-calculator" :class="calculatorName">

    <div class="row justify-content-center calculator-header">
      <CalculatorHeader>
        <h2>Remortgage Calculator</h2>
        <h4>Complete your income details below to give yourselves an idea how much you may be able to borrow.</h4>
      </CalculatorHeader>
    </div>

    <div class="row justify-content-center calculator-body">
      <div class="col-md-10 col-xl-8">
        <form class="calculator-container">

          <div class="mt-3" @keyup="resetCalculation" @change="resetCalculation">

            <BooleanChoiceButtons
                fieldText="Number of Applicants"
                buttonTextLeft="1"
                buttonTextRight="2"
            >
              <template v-slot:left>
                <input type="radio" v-model="applicantsCount" value=1 @click="clearApplicant2">
              </template>
              <template v-slot:right>
                <input type="radio" v-model="applicantsCount" value=2>
              </template>
            </BooleanChoiceButtons>

            <transition name="fade-x" mode="out-in">
              <div class="row mb-5" v-if="applicantsCount > 0">
                <h4 class="calc-fieldset-title">First Applicant</h4>
                <InputElement fieldText="Applicant 1 annual income (including benefits)?">
                  <InputTypeNumber currencyPlaceholder v-model="annualIncome1"/>
                </InputElement>
              </div>
            </transition>

            <transition name="fade-x" mode="out-in">
              <div class="row mb-5" v-if="applicantsCount == 2">
                <h4 class="calc-fieldset-title">Second Applicant</h4>
                <InputElement fieldText="Applicant 2 annual income (including benefits)?">
                  <InputTypeNumber currencyPlaceholder v-model="annualIncome2"/>
                </InputElement>
              </div>
            </transition>

            <transition name="scale-in" mode="out-in">
              <ReturnedValueBox
                  class="mb-5 mt-5"
                  text="Your household income is: "
                  :value="householdIncome"
                  v-if="householdIncome > 0"
                  :innerElement=true
              />
            </transition>

            <transition name="scale-in" mode="out-in">
              <div class="row mb-5" v-if="applicantsCount > 0 && annualIncome1 > 0">
                <h4 class="calc-fieldset-title">First Applicant Monthy Commitments</h4>
                <InputElement fieldText="HP (Hire purchase)">
                  <InputTypeNumber currencyPlaceholder v-model="hirePurchase1"/>
                </InputElement>

                <transition name="fade-x" mode="out-in">
                  <InputElement
                      v-show="applicantsCount > 0 && annualIncome1 > 0 && hirePurchase1 !== null"
                      fieldText="Loans"
                  >
                    <InputTypeNumber currencyPlaceholder v-model="loans1"/>
                  </InputElement>
                </transition>

                <transition name="fade-x" mode="out-in">
                  <InputElement
                      v-show="applicantsCount > 0 && annualIncome1 > 0 && hirePurchase1 !== null && loans1 !== null"
                      fieldText="Student Loan"
                  >
                    <InputTypeNumber currencyPlaceholder v-model="studentLoan1"/>
                  </InputElement>
                </transition>

                <transition name="fade-x" mode="out-in">
                  <InputElement
                      v-show="applicantsCount > 0 && annualIncome1 > 0 && hirePurchase1 !== null && loans1 !== null && studentLoan1 !== null"
                      fieldText="Child Maintenance Payments"
                  >
                    <InputTypeNumber currencyPlaceholder v-model="childPayments1"/>
                  </InputElement>
                </transition>

                <transition name="fade-x" mode="out-in">
                  <InputElement
                      v-show="applicantsCount > 0 && annualIncome1 > 0 && hirePurchase1 !== null && loans1 !== null && studentLoan1 !== null && childPayments1 !== null"
                      fieldText="Credit Card Payments (min 3% outstanding balance)"
                  >
                    <InputTypeNumber currencyPlaceholder v-model="cardPayments1"/>
                  </InputElement>
                </transition>
              </div>
            </transition>

            <transition name="scale-in" mode="out-in">
              <div class="row mb-5" v-if="applicantsCount == 2 && annualIncome2 > 0">
                <h4 class="calc-fieldset-title">Second Applicant Monthy Commitments</h4>
                <InputElement fieldText="HP (Hire purchase)">
                  <InputTypeNumber currencyPlaceholder v-model="hirePurchase2"/>
                </InputElement>

                <transition name="fade-x" mode="out-in">
                  <InputElement
                      v-show="applicantsCount == 2 && annualIncome2 > 0 && hirePurchase2 !== null"
                      fieldText="Loans"
                  >
                    <InputTypeNumber currencyPlaceholder v-model="loans2"/>
                  </InputElement>
                </transition>

                <transition name="fade-x" mode="out-in">
                  <InputElement
                      v-show="applicantsCount == 2 && annualIncome2 > 0 && hirePurchase2 !== null && loans2 !== null"
                      fieldText="Student Loan"
                  >
                    <InputTypeNumber currencyPlaceholder v-model="studentLoan2"/>
                  </InputElement>
                </transition>

                <transition name="fade-x" mode="out-in">
                  <InputElement
                      v-show="applicantsCount == 2 && annualIncome2 > 0 && hirePurchase2 !== null && loans2 !== null && studentLoan2 !== null"
                      fieldText="Child Maintenance Payments"
                  >
                    <InputTypeNumber currencyPlaceholder v-model="childPayments2"/>
                  </InputElement>
                </transition>

                <transition name="fade-x" mode="out-in">
                  <InputElement
                      v-show="applicantsCount == 2 && annualIncome2 > 0 && hirePurchase2 !== null && loans2 !== null && studentLoan2 !== null && childPayments2 !== null"
                      fieldText="Credit Card Payments (min 3% outstanding balance)"
                  >
                    <InputTypeNumber currencyPlaceholder v-model="cardPayments2"/>
                  </InputElement>
                </transition>
              </div>
            </transition>

            <transition name="fade-x" mode="out-in">
              <div class="row mt-5" v-if="showCalculateButton">
                <div class="col-md-6 col-lg-4">Why are you using the calculator today</div>
                <div class="col-md-6 col-lg-8 fcs-grid-long">
                  <DropdownMenu :dropdownOptions="dropdownOptionsReason" v-model="calcReason1">
                </div>
              </div>
            </transition>

            <transition name="fade-x" mode="out-in">
              <div class="row mt-5 mb-5" v-if="showCalculateButton">
                <div class="col-md-6 col-lg-4">When will you be ready to move forward?</div>
                <div class="col-md-6 col-lg-8 fcs-grid-long">
                  <DropdownMenu :dropdownOptions="dropdownOptionsMove" v-model="calcReason2">
                </div>
              </div>
            </transition>

            <!-- <transition name="fade-x" mode="out-in">
							<InputElement
								v-if="showListFields"
								fieldText="Name"
							>
								<input class="form-control calc-form-control" type="text" v-model="listName">
							</InputElement>
						</transition>

						<transition name="fade-x" mode="out-in">
							<InputElement
								v-if="showListFields"
								fieldText="Email Address"
							>
								<input class="form-control calc-form-control" type="email" v-model="emailAddress">
							</InputElement>
						</transition>

						<transition name="fade-x" mode="out-in">
							<div class="row align-items-center" v-if="showListFields">
								<div class="col-md-1">
									<input type="checkbox" id="privacy" v-model="consent" >
								</div>
								<div class="col-md-11">
									<label for="privacy">I consent to my personal information being processed in accordance with The Mortgage Centres <a href="/cookies-privacy-statement/" target="_blank">Privacy Policy</a></label>
								</div>
							</div>
						</transition> -->

            <transition name="scale-in" mode="out-in">
              <div
                  v-if="showCalculateButton"
                  class="text-center mt-5 mb-3"
              >
                <div class="btn calc-btn-secondary" @click="calculate">CALCULATE</div>
              </div>
            </transition>

          </div>

          <transition name="fade-y" mode="out-in">
            <div v-if="calcCompleted" ref="resultsContainer">

              <ResultsContainer class="mt-5 mb-5 calc-results-box">
                <div class="mb-3 results-title">You may be able to borrow:</div>
                <span class="fcs-price">£{{ formatNumberOutput(requiredLoanValue) }}</span>
                <div class="mt-3">Please submit your details below to obtain your most suitable advice on mortgage
                  affordability and the most suitable rates.
                </div>
              </ResultsContainer>

              <SubmissionForm :calculatorName="calculatorName" :calculatorData="collateMessageBody"/>

            </div>
          </transition>

        </form>
      </div>
    </div>

  </div>
</template>

<script>
import BooleanChoiceButtons from '@/components/utilities/booleanChoiceButtons.vue'
import CalculatorHeader from '@/components/utilities/calculatorHeader.vue'
import InputElement from '@/components/utilities/inputElement.vue'
import InputTypeNumber from '@/components/utilities/inputTypeNumber.vue'
import ResultsContainer from '@/components/utilities/resultsContainer.vue'
import ReturnedValueBox from '@/components/utilities/returnedValueBox.vue'
import SubmissionForm from '@/components/utilities/submissionForm.vue'
import DropdownMenu from '@/components/utilities/dropdownMenu.vue'
import axios from "axios"

export default {
  components: {
    BooleanChoiceButtons,
    CalculatorHeader,
    InputElement,
    InputTypeNumber,
    ResultsContainer,
    ReturnedValueBox,
    DropdownMenu,
    SubmissionForm
  },
  data() {
    return {
      calculatorName: 'remortgage-calculator',
      applicantsCount: 0,

      annualIncome1: null,
      hirePurchase1: null,
      loans1: null,
      studentLoan1: null,
      childPayments1: null,
      cardPayments1: null,

      annualIncome2: null,
      hirePurchase2: null,
      loans2: null,
      studentLoan2: null,
      childPayments2: null,
      cardPayments2: null,

      calculatorReasonTitle: null,
      calculatorReasonResults: null,

      calcReason1: "",
      calcReason2: "",
      listName: "",
			emailAddress: "",
			consent: false,
      dropdownOptionsReason: [
        {value: "Please select an option", text: "Please select an option"},
        {value: "I'm ready to move forward with a mortgage", text: "I'm ready to move forward with a mortgage"},
        {
          value: "I'm not ready for a mortgage but am curious about what I could borrow",
          text: "I'm not ready for a mortgage but am curious about what I could borrow"
        },
        {
          value: "I'm starting to save for a house and want to know what to aim for",
          text: "I'm starting to save for a house and want to know what to aim for"
        },
        {
          value: "I need to understand a what I can afford before house shopping",
          text: "I need to understand a what I can afford before house shopping"
        },
      ],

      dropdownOptionsMove: [
        {value: "Please select an option", text: "Please select an option"},
        {value: "12+ months", text: "12+ months"},
        {value: "6-12 months", text: "6-12 months"},
        {value: "In the next 3 months", text: "In the next 3 months"},
        {value: "As soon as possible", text: "As soon as possible"},
      ],

      requiredLoanValue: 0,
      calcCompleted: false,

      submissionReason: {
        submitted: false,
        is_valid: false,
        confirmation_message: ''
      },
      submissionList: {
				submitted: false,
				is_valid: false,
				confirmation_message: ''
			},
      formErrors: [],
      gfid: 2, //Please check the Gravity Form for corresponding form id (staging and live may vary!)
			gfidlist: 7 //Please check the Gravity Form for corresponding form id (staging and live may vary!)
    }
  },
  methods: {
    calculate() {
      let requiredLoanValue1
      let requiredLoanValue2
      this.applicantsCount > 0 ? requiredLoanValue1 = ((this.annualIncome1 - ((this.hirePurchase1 + this.loans1 + this.studentLoan1 + this.childPayments1 + this.cardPayments1) * 12)) * 4.85) : requiredLoanValue1 = 0
      this.applicantsCount == 2 ? requiredLoanValue2 = ((this.annualIncome2 - ((this.hirePurchase2 + this.loans2 + this.studentLoan2 + this.childPayments2 + this.cardPayments2) * 12)) * 4.85) : requiredLoanValue2 = 0
      this.requiredLoanValue = requiredLoanValue1 + requiredLoanValue2
      this.calcCompleted = true;
      this.calculatorReasonTitle = this.removeHyphensToUpperCase(this.calculatorName);
      this.calculatorReasonResults =
          `
			Why are you using the calculator today: ${this.calcReason1}
			When will you be ready to move forward: ${this.calcReason2}
			`;
      this.$nextTick(() => {
        const resultsContainer = this.$refs.resultsContainer;

        if (resultsContainer) {
          // Get the bounding rectangle of the element
          const rect = resultsContainer.getBoundingClientRect();

          // Calculate the target scroll position
          const targetScrollPosition = window.scrollY + rect.top - 200;

          // Scroll to the target position
          window.scrollTo({
            top: targetScrollPosition,
            behavior: 'smooth'
          });
        } else {
          console.error('resultsContainer is undefined');
        }
      });
      var newReasonSubmission = new FormData();
			var newListSubmission = new FormData();

      newReasonSubmission.append("form_id", this.gfid); //Please check the Gravity Form form id (staging and live may vary!)
      newReasonSubmission.append("input_1", this.calculatorReasonTitle);
      newReasonSubmission.append("input_3", this.calculatorReasonResults);

      newListSubmission.append("form_id", this.gfidlist); //Please check the Gravity Form form id (staging and live may vary!)
			newListSubmission.append("input_1", this.listName);
			newListSubmission.append("input_3", this.emailAddress);
			newListSubmission.append("input_4.1", this.consent ? 'Yes' : 'No');

      this.postReasonSubmission(newReasonSubmission);
      this.postListSubmission(newListSubmission);
    },
    async postReasonSubmission(submissionReasonData) {
      try {

        const response = await axios.post(
            `/wp-json/gf/v2/forms/` + this.gfid + `/submissions`, //Please check the Gravity Form form id (staging and live may vary!)
            submissionReasonData,
            {
              headers: {"Content-Type": "application/x-www-form-urlencoded"}
            }
        );

        const submissionReason = response;
        // console.log(submission)

        if (submissionReason.data.is_valid) {
          this.submissionReason.submitted = true;
          this.submissionReason.is_valid = true;
        }
      } catch (error) {
        // console.log(error)
      }
    },
    async postListSubmission(submissionListData) {
            try {

                const response = await axios.post(
                    `/wp-json/gf/v2/forms/`+ this.gfidlist + `/submissions`, //Please check the Gravity Form form id (staging and live may vary!)
                    submissionListData,
                    {
                        headers: {"Content-Type": "application/x-www-form-urlencoded"}
                    }
                );
                
                const submissionList = response;
                // console.log(submission)

                if ( submissionList.data.is_valid ) {
                    this.submissionList.submitted = true;
                    this.submissionList.is_valid = true;
                }
            } catch(error) {
                // console.log(error)
            }
        },
    clearApplicant2() {
      this.annualIncome2 = null,
          this.hirePurchase2 = null,
          this.loans2 = null,
          this.studentLoan2 = null,
          this.childPayments2 = null,
          this.cardPayments2 = null
    },
    resetCalculation() {
      this.calcCompleted = false
    },
    formatNumberOutput(number) {
      const newNumber = (new Intl.NumberFormat('en-GB').format(number))
      if (newNumber === 'NaN') {
        return 0
      }
      return newNumber
    },
    removeHyphensToUpperCase(str) {
      return str.replace(/(-|^)([^-]?)/g, function (_, prep, letter) {
        return (prep && ' ') + letter.toUpperCase();
      });
    },
    formatEmptyMessageFields(message) {
      return message ? message : 'N/A'
    }
  },
  computed: {
    /**
     * Show button if previous section is completed
     * @return {Bool}
     */
    showListFields() {
			let listFields
      if (this.applicantsCount < 1) {
        return listFields = false
      }
      this.applicantsCount == 1
          ? (this.annualIncome1 > 0 && this.hirePurchase1 !== null && this.loans1 !== null && this.studentLoan1 !== null && this.childPayments1 !== null && this.cardPayments1 !== null) ? listFields = true : listFields = false
          : (this.annualIncome2 > 0 && this.hirePurchase2 !== null && this.loans2 !== null && this.studentLoan2 !== null && this.childPayments2 !== null && this.cardPayments2 !== null) && (this.annualIncome1 !== null && this.hirePurchase1 !== null && this.loans1 !== null && this.studentLoan1 !== null && this.childPayments1 !== null && this.cardPayments1 !== null) ? listFields = true : listFields = false
      return listFields
		},
    showCalculateButton() {
      let calculateButton
      if (this.applicantsCount < 1) {
        return calculateButton = false
      }
      this.applicantsCount == 1
          ? (this.annualIncome1 > 0 && this.hirePurchase1 !== null && this.loans1 !== null && this.studentLoan1 !== null && this.childPayments1 !== null && this.cardPayments1 !== null) ? calculateButton = true : calculateButton = false
          : (this.annualIncome2 > 0 && this.hirePurchase2 !== null && this.loans2 !== null && this.studentLoan2 !== null && this.childPayments2 !== null && this.cardPayments2 !== null) && (this.annualIncome1 !== null && this.hirePurchase1 !== null && this.loans1 !== null && this.studentLoan1 !== null && this.childPayments1 !== null && this.cardPayments1 !== null) ? calculateButton = true : calculateButton = false
      return calculateButton
    },
    householdIncome() {
      let annualIncome1 = this.annualIncome1 ? this.annualIncome1 : 0;
      let annualIncome2 = this.annualIncome2 ? this.annualIncome2 : 0;

      return annualIncome1 + annualIncome2;
    },
    collateMessageBody() {
      const calculatorData =
          {
            calculatorTitle: this.removeHyphensToUpperCase(this.calculatorName),
            calculatorResults:
                `
					Applicant 1
                        Annual Income: £${this.formatEmptyMessageFields(this.annualIncome1)}
                        HP (Hire purchase): £${this.formatEmptyMessageFields(this.hirePurchase1)}
                        Loans: £${this.formatEmptyMessageFields(this.loans1)}
                        Student Loan: £${this.formatEmptyMessageFields(this.studentLoan1)}
                        Child Maintenance Payments £${this.formatEmptyMessageFields(this.childPayments1)}
                        Credit Card Payments: £${this.formatEmptyMessageFields(this.cardPayments1)}
                    Applicant 2
                        Annual Income: £${this.formatEmptyMessageFields(this.annualIncome2)}
                        HP (Hire purchase): £${this.formatEmptyMessageFields(this.hirePurchase2)}
                        Loans: £${this.formatEmptyMessageFields(this.loans2)}
                        Student Loan: £${this.formatEmptyMessageFields(this.studentLoan2)}
                        Child Maintenance Payments £${this.formatEmptyMessageFields(this.childPayments2)}
                        
                    Household Annual Income: £${this.householdIncome}
                    Required Loan Value: £${this.requiredLoanValue}
                    Why are you using the calculator today: ${this.calcReason1}
                    When will you be ready to move forward: ${this.calcReason2}
                `
          }
      return calculatorData;
    }
  }
}
</script>

<style lang="scss">
@import './styles/calcs.scss';
</style>